import React, { useEffect, useMemo, useRef, useState } from 'react';
import { groupPortalAccounts, PortalAccountGroup, splitAccountName, portalKeyForAccount, PORTAIS_COM_AUTOMACAO } from './procurementUi';
import { campo } from '../../design';

/**
 * Seletor da CREDENCIAL de portal no modal Nova/Editar Proposta.
 *
 * ⛔ SUBSTITUI UM <select> NATIVO que listava as credenciais de Acessos em
 * ordem alfabética como "Banrisul (Fabio) (todos)", "BLL Compras (todos)"… Duas
 * coisas se perdiam ali, e a primeira é a que importa:
 *
 *   1. quais portais têm AUTOMAÇÃO. Cadastrar a proposta é justamente quando a
 *      pessoa decide se vai depender do robô ou fazer no braço, e a lista não
 *      dizia. O PortalSelect do painel lateral já separava isso; as duas telas
 *      respondiam a mesma pergunta de dois jeitos.
 *   2. a diferença entre PORTAL e CONTA. "Banrisul (Fabio)" e "Banrisul (Mae)"
 *      apareciam como itens irmãos de "BLL Compras". Aqui a conta é segundo
 *      nível dentro do portal.
 *
 * O "(todos)" era o `system` da credencial — igual em todas, não explicava
 * nada — e saiu.
 *
 * A classificação usa `portalKeyForAccount`, a MESMA do "✓" do PortalSelect,
 * para as duas telas nunca discordarem sobre qual portal tem robô.
 */

interface Account {
  id: number | string;
  portalName?: string | null;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  accounts: Account[];
  disabled?: boolean;
}

export default function PortalAccountSelect({ value, onChange, accounts, disabled }: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const caixaRef = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!aberto) return;
    buscaRef.current?.focus();
    const fora = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setAberto(false); } // o Esc é do popover: a casca de fora não fecha junto
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const selecionada = useMemo(() => {
    const acc = accounts.find((a) => String(a.id) === value);
    if (!acc) return null;
    const { base, conta } = splitAccountName(acc.portalName);
    const key = portalKeyForAccount(base);
    return { base, conta, automacao: !!key && PORTAIS_COM_AUTOMACAO.has(key) };
  }, [accounts, value]);

  const { comAutomacao, semAutomacao } = useMemo(() => {
    const grupos = groupPortalAccounts(accounts);
    const termo = busca.trim().toLowerCase();
    if (!termo) return grupos;
    // Busca casa no portal OU na conta: digitar "mae" tem de achar "Banrisul (Mae)".
    const filtra = (gs: PortalAccountGroup[]) =>
      gs
        .map((g) =>
          g.base.toLowerCase().includes(termo)
            ? g
            : { ...g, contas: g.contas.filter((c) => (c.conta || '').toLowerCase().includes(termo)) },
        )
        .filter((g) => g.contas.length > 0);
    return { comAutomacao: filtra(grupos.comAutomacao), semAutomacao: filtra(grupos.semAutomacao) };
  }, [accounts, busca]);

  const escolher = (id: string) => {
    setAberto(false);
    setBusca('');
    if (id !== value) onChange(id);
  };

  const Linha = ({ id, rotulo, recuo }: { id: string; rotulo: string; recuo?: boolean }) => (
    <button
      type="button"
      onClick={() => escolher(id)}
      className={`w-full text-left py-1.5 rounded text-sm transition-colors ${recuo ? 'pl-6 pr-2' : 'px-2'} ${
        id === value ? 'bg-orange-500/20 text-orange-200' : 'text-slate-300 hover:bg-slate-700/70'
      }`}
    >
      {rotulo}
    </button>
  );

  const Grupo = ({ g }: { g: PortalAccountGroup }) => {
    // Portal com uma credencial só é uma linha; com várias, o nome do portal
    // vira cabeçalho e as contas ficam embaixo. A conta sem sufixo aparece
    // como "principal" para não ficar uma linha vazia.
    if (g.contas.length === 1) {
      const c = g.contas[0];
      return <Linha id={c.id} rotulo={c.conta ? `${g.base} · ${c.conta}` : g.base} />;
    }
    return (
      <div>
        <p className="px-2 pt-1.5 pb-0.5 text-sm text-slate-400">{g.base}</p>
        {g.contas.map((c) => (
          <Linha key={c.id} id={c.id} rotulo={c.conta || 'principal'} recuo />
        ))}
      </div>
    );
  };

  const vazio = !comAutomacao.length && !semAutomacao.length;

  return (
    <div className="relative" ref={caixaRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAberto((v) => !v)}
        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-left flex items-center gap-2 disabled:opacity-50"
      >
        <span className={`min-w-0 flex-1 truncate ${selecionada ? 'text-white' : 'text-slate-400'}`}>
          {selecionada ? (selecionada.conta ? `${selecionada.base} · ${selecionada.conta}` : selecionada.base) : 'Selecione o portal...'}
        </span>
        {selecionada && (
          <span
            className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${
              selecionada.automacao ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-600/60 text-slate-400'
            }`}
          >
            {selecionada.automacao ? 'com automação' : 'manual'}
          </span>
        )}
        <span className="shrink-0 text-slate-400">▾</span>
      </button>

      {aberto && (
        <div className="absolute z-50 mt-1 left-0 right-0 min-w-[260px] bg-slate-800 border border-slate-600 rounded-lg shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-slate-700">
            <input
              ref={buscaRef}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar portal ou conta…"
              className={`w-full rounded px-2 py-1 text-xs outline-none ${campo}`}
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto p-1.5 space-y-0.5">
            {vazio && <p className="text-xs text-slate-500 px-2 py-3 text-center">Nenhum portal encontrado</p>}

            {comAutomacao.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-emerald-400/80 px-2 pt-1 pb-0.5">
                  Com automação — o robô envia e disputa
                </p>
                {comAutomacao.map((g) => (
                  <Grupo key={g.base} g={g} />
                ))}
              </>
            )}

            {semAutomacao.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 px-2 pt-2 pb-0.5">
                  Sem automação — envio e disputa no braço
                </p>
                {semAutomacao.map((g) => (
                  <Grupo key={g.base} g={g} />
                ))}
              </>
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-700 text-[10px] text-slate-500 leading-snug">
            Lista as credenciais cadastradas em <strong className="text-slate-400">Acessos</strong>; portal com mais de
            uma conta mostra as contas embaixo do nome.
          </div>
        </div>
      )}
    </div>
  );
}
